import axiosInstance from '../config/axiosConfig';
import { TokenStore } from './TokenStore';
import { ProfileRejectPayload } from '../config/profileRejectionReasons';

interface GetUsersFilters {
  page?: number;
  limit?: number;
  status?: string;
  duration?: string;
  search?: string;
  eligible?: boolean;
  startDate?: string;
  endDate?: string;
}
interface GetFlaggedSubmissionFilters {
  page?: number;
  limit?: number;
  search?: string;
  filter?: string;
}

interface ModifyStatusBody {
  status: string;
  reasonToReject?: string;
  rejectionReasonCode?: string;
  rejectionReasonNote?: string;
}

export const getUsers = async (filters: GetUsersFilters): Promise<any> => {
  try {
    const token = TokenStore.getActiveAccessToken();
    const response = await axiosInstance.get('/admin/get-users', {
      params: filters,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return {
      users: response.data.data.users || [],
      pagination: response.data.data.pagination || {
        total: 0,
        page: 1,
        limit: 8,
        totalPages: 1,
      },
    };
  } catch (error) {
    console.error('Error fetching users', error);
    throw error;
  }
};

export const modifyUserStatus = async (
  userId: string,
  status: string,
  rejection?: ProfileRejectPayload,
): Promise<void> => {
  try {
    const token = TokenStore.getActiveAccessToken();
    const body: ModifyStatusBody = { status };
    if (status === 'rejected' && rejection) {
      // `reasonToReject` stays on the wire for an API that predates the reason
      // catalogue; the code is what a current API expands into the app's copy.
      body.reasonToReject = rejection.reasonToReject;
      body.rejectionReasonCode = rejection.rejectionReasonCode;
      if (rejection.rejectionReasonNote) {
        body.rejectionReasonNote = rejection.rejectionReasonNote;
      }
    }
    const response = await axiosInstance.put(
      `/admin/modify-status/${userId}`,
      body,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (!response.data.success) {
      throw new Error('Status update failed');
    }
  } catch (error) {
    console.error('Error modifying user status:', error);
    throw error;
  }
};

export const getCreatorSocialData = async (userId: string): Promise<any> => {
  try {
    const token = TokenStore.getActiveAccessToken();
    const response = await axiosInstance.get(`/admin/user-profile/${userId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching creator social data:', error);
    throw error;
  }
};

export const getUserById = async (userId: string): Promise<any> => {
  try {
    const response = await axiosInstance.get(`/users/app/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching creator social data:', error);
    throw error;
  }
};

/**
 * Queues a creator-silent social score recalculation. The backend enforces a
 * 5-minute per-creator cooldown and answers 429 + retryAfterSeconds inside
 * the window — the error must propagate so the caller can show the countdown.
 */
export const refreshCreatorSocialScore = async (
  userId: string,
): Promise<{ success: boolean; message: string }> => {
  const response = await axiosInstance.post(
    `/users/admin/${userId}/social-score/refresh`,
  );
  return response.data;
};

export const ProfileCompletion = async (userId: string) => {
  try {
    const token = TokenStore.getActiveAccessToken();
    const response = await axiosInstance.get(
      `/users/${userId}/profile-completion`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error('Error when fetching user profile completion', error);
    return null;
  }
};

export const getFlaggedSubmissions = async (
  filters: GetFlaggedSubmissionFilters,
): Promise<any> => {
  try {
    const token = TokenStore.getActiveAccessToken();
    const response = await axiosInstance.get('/admin/get-aiRejected-cashback', {
      params: filters,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return {
      data: response.data.data || [],
      pagination: {
        total: response.data.total || 0,
        page: response.data.page || 1,
        limit: response.data.limit || 6,
        totalPages: response.data.totalPages || 1,
      },
    };
  } catch (error) {
    console.error('Error fetching users', error);
    return null;
  }
};
/**
 * Approve or reject a cashback.
 *
 * DO NOT wrap this in a try/catch that returns null. It used to, and on
 * 2026-08-26 that caused a double payout on production: the backend returned
 * 400, the catch turned it into a resolved `null`, so `await` succeeded in the
 * caller and it ran the payment call on the next line.
 *
 * The legacy /connectPay/withdraw deposit is gone here, so a swallowed error no
 * longer moves money — but it still makes a FAILED approval look successful:
 * removeItemFromCache drops the row from the list and a DEPOSIT log line is
 * written for a credit that never happened.
 *
 * Errors must reach the caller so it can stop and show them.
 */
export const verifyContent = async (
  cashbackId: string,
  body: {
    isApproved: boolean;
    reasonToReject?: string;
    bonusAmount?: number;
    bonusNote?: string;
  },
) => {
  const response = await axiosInstance.put(
    `/admin/verify-content/${cashbackId}`,
    { ...body },
  );
  return response.data;
};

export const getCashbackDuplicateComparison = async (
  cashbackId: string,
): Promise<any> => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await axiosInstance.get(
      `/admin/duplicate-comparison/${cashbackId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return response.data.data;
  } catch (error) {
    console.error('Error fetching duplicate comparison', error);
    return null;
  }
};

export const createLogs = async (userId: string, type: string, meta?: any) => {
  try {
    const response = await axiosInstance.post(`/log/createLog`, {
      userId,
      type,
      meta,
    });

    return response.data;
  } catch (error) {
    console.error('Error in the create logs', error);
    return null;
  }
};

export interface CashbackLedgerFilters {
  page?: number;
  limit?: number;
  campaignId?: string;
  platform?: string;
  dateFrom?: string;
  dateTo?: string;
  userId?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  aiStatus?: string;
  search?: string;
  /** Restrict to rows carrying a discretionary bonus — the bonus ledger. */
  hasBonus?: boolean;
}

export const getCashbackLedger = async (
  filters: CashbackLedgerFilters,
): Promise<any> => {
  try {
    const token = TokenStore.getActiveAccessToken();
    const response = await axiosInstance.get('/admin/cashbacks', {
      params: filters,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data;
  } catch (error) {
    console.error('Error fetching cashback ledger', error);
    throw error;
  }
};

export const getCashbackLedgerGrouped = async (
  filters: CashbackLedgerFilters,
): Promise<any> => {
  try {
    const token = TokenStore.getActiveAccessToken();
    const response = await axiosInstance.get('/admin/cashbacks/grouped', {
      params: filters,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data;
  } catch (error) {
    console.error('Error fetching grouped cashback ledger', error);
    throw error;
  }
};

export const getCashbackDetailService = async (id: string): Promise<any> => {
  try {
    const token = TokenStore.getActiveAccessToken();
    const response = await axiosInstance.get(`/admin/cashbacks/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data;
  } catch (error) {
    console.error('Error fetching cashback detail', error);
    throw error;
  }
};

export const retriggerAi = async (cashbackId: string): Promise<void> => {
  await axiosInstance.post(`/admin/cashbacks/${cashbackId}/retrigger-ai`);
};

export interface CampaignOption {
  campaignId: string;
  title: string;
  status: string;
  startDate: string | null;
}

export interface BrandOption {
  brandId: string;
  brandName: string;
  campaigns: CampaignOption[];
}

export interface FilterOptionsResult {
  brands: BrandOption[];
}

export const getCashbackFilterOptions =
  async (): Promise<FilterOptionsResult> => {
    const token = TokenStore.getActiveAccessToken();
    const response = await axiosInstance.get(
      '/admin/cashbacks/filter-options',
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data.data as FilterOptionsResult;
  };

export interface CreatorOption {
  userId: string;
  userName: string;
}

export interface CreatorOptionsResult {
  creators: CreatorOption[];
}

export const getCreatorOptions = async (
  campaignId: string,
): Promise<CreatorOptionsResult> => {
  const token = TokenStore.getActiveAccessToken();
  const response = await axiosInstance.get('/admin/cashbacks/creator-options', {
    params: { campaignId },
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.data as CreatorOptionsResult;
};
